import { Stack } from "@mui/material";
import * as Sentry from "@sentry/react";
import React from "react";
import { FormattedMessage } from "react-intl";

import useToken from "../../../hooks/useToken";
import { useAlert } from "../../../lib/Alerts";
import { authHeader, formsFetch, unwrap } from "../../../lib/formsApi";
import { FileListItem } from "../FileListItem";

interface FilesListProps {
    fileNames: string[];
    onFileRemove: (fileName: string) => void;
    disabled?: boolean;
    answerId: string;
    setLoading: React.Dispatch<React.SetStateAction<boolean>>;
}

export const FilesList: React.FC<FilesListProps> = ({ fileNames, onFileRemove, disabled, answerId, setLoading }) => {
    const { token } = useToken();
    const { alertUser } = useAlert();

    const handleFileRemove = async (fileName: string) => {
        setLoading(true);
        try {
            const result = await formsFetch.DELETE("/api/files/{referenceId}/{fileName}", {
                headers: authHeader(token),
                params: { path: { referenceId: answerId, fileName } },
            });
            if (result.response.status !== 404) {
                unwrap(result, "delete file");
            }
            onFileRemove(fileName);
        } catch (deleteError) {
            Sentry.captureException(deleteError, {
                extra: { fileName, answerId, context: "File delete error" },
            });
            alertUser({
                value: <FormattedMessage defaultMessage="Could not remove the file. Please try again." />,
                severity: "error",
            });
        } finally {
            setLoading(false);
        }
    };

    return (
        <Stack gap={1}>
            {fileNames?.map((fileName) => (
                <FileListItem
                    key={fileName}
                    fileName={fileName}
                    answerId={answerId}
                    onDeleteFile={handleFileRemove}
                    disabled={disabled}
                />
            ))}
        </Stack>
    );
};
